import React from 'react';
import { Badge, Tabs, TabsProps } from 'antd';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

type TabItemsType = TabsProps['items'];

const TabLabelWithBadge = styled.span`
  position: relative;
  display: inline-block;
  padding-right: 10px;
`;

const TabBadge = styled(Badge)`
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
`;

type TabProps = {
  tabNames: string[];
  tabBadge?: Record<string, React.ReactNode>;
  tabContent: React.ReactNode[];
  changeActiveTab?: (key: string) => void;
  bordered?: boolean;
  mountAllTabContents?: boolean;
  useTabNamesAsKeys?: boolean;
} & React.ComponentProps<typeof Tabs>;

const Tab = ({
  tabNames,
  tabBadge,
  tabContent,
  changeActiveTab = undefined,
  bordered = false,
  useTabNamesAsKeys = false,
  mountAllTabContents = false,
  ...rest
}: TabProps) => {
  const firstTabKey = useTabNamesAsKeys ? tabNames[0] : '0';

  const activeKey = (() => {
    if (rest?.activeKey !== '') return rest.activeKey;
    if (changeActiveTab) return firstTabKey;
    return undefined;
  })();

  const tabItems: TabItemsType = tabNames.map((tabName, index) => {
    const badge = tabBadge?.[tabName];
    const testId = `tab-${tabName.replace(/\s/g, '-').toLowerCase()}`;

    return {
      label: badge ? (
        <TabLabelWithBadge title={tabName} data-testid={testId}>
          {tabName}
          <TabBadge count={badge} size="small" />
        </TabLabelWithBadge>
      ) : (
        <span title={tabName} data-testid={testId}>
          {tabName}
        </span>
      ),
      key: tabName,
      ...(!mountAllTabContents && {
        children: (
          <div
            css={css`
              overflow: auto;
              padding: 0 0 1em 0;
            `}
          >
            {tabContent[index]}
          </div>
        )
      })
    };
  });

  return (
    <div
      className={bordered ? 'bordered' : ''}
      css={css`
        background: #fff;
        min-height: max-content;

        &.bordered {
          border-radius: 10px;
          box-shadow: 0 3px 6px #0000004d;
        }
      `}
    >
      <Tabs
        activeKey={activeKey}
        onChange={changeActiveTab}
        tabBarStyle={bordered ? { padding: '0 1em' } : {}}
        items={tabItems}
      />

      {mountAllTabContents &&
        tabNames.map((tabName, index) => {
          const key = useTabNamesAsKeys ? tabName : String(index);
          return (
            <div
              key={key}
              css={css`
                overflow: auto;
                padding: 0 0 1em 0;
                display: ${activeKey === key ? 'block' : 'none'};
              `}
            >
              {tabContent[index]}
            </div>
          );
        })}
    </div>
  );
};

export default Tab;
